import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, inject } from '@angular/core';

import { IConstant, IOutcome, ISportEvent } from '@frontend/oxygen/shared-utils';

import { EnvironmentConfig } from '../../../client-config/index';
import { CmsService } from '../../../core/services/cms/cms.service';
import { PubSubService } from '../../../core/services/communication/pubsub/pubsub.service';
import { DeviceService } from '../../../core/services/device/device.service';
import { GtmService } from '../../../core/services/gtm/gtm.service';
import { GtmTrackingService } from '../../../core/services/gtmTracking/gtm-tracking.service';
import { WindowRefService } from '../../../core/services/windowRef/window-ref.service';
import { MarketsGroupComponent } from '../../../edp/components/marketsGroup/markets-group.component';
import { IGroupedMarket, IGroupedMarketHeader } from '../../../edp/services/marketsGroup/markets-group.model';
import { SharedModule } from '../../../shared/shared.module';

@Component({
    selector: 'player-bets',
    imports: [CommonModule, SharedModule],
    templateUrl: './player-bets.component.html',
    styleUrl: './player-bets.component.scss',
})
export class PlayerBetsComponent extends MarketsGroupComponent implements AfterViewInit, OnDestroy {
    @Input() override marketsGroup: IGroupedMarket;
    @Input() override periodIndex: number;
    @Input() override limitCount: number;
    @Input() override eventEntity: ISportEvent;
    @Output() readonly isLoaded = new EventEmitter();
    isNext: boolean = true;
    private readonly ODDS_TABLE_PADDING = 12;
    private oddsTablesforplayerBets: HTMLElement | null;
    private pricebtnWidth: HTMLElement | null;
    override isDesktop: boolean;
    isCoral: boolean;
    isNextAvailableOuter: boolean;
    isNextavailableInner: boolean;
    isPrevAvailablePB: boolean;
    isLoadedPB: boolean = false;
    private readonly environment = inject(EnvironmentConfig).configuration;
    constructor(
        protected override pubSubService: PubSubService,
        protected override changeDetectorRef: ChangeDetectorRef,
        protected override cmsService: CmsService,
        protected override windowRefService: WindowRefService,
        protected override deviceService: DeviceService,
        public override gtmService: GtmService,
        public override gtmTrackingService: GtmTrackingService,
    ) {
        super(pubSubService, changeDetectorRef, cmsService, windowRefService, deviceService, gtmService, gtmTrackingService);
        this.isCoral = this.environment && this.environment.BRAND === 'bma';
        this.isDesktop = this.deviceService.isDesktop && !this.deviceService.isMobile;
    }

    ngAfterViewInit() {
        if (this.isDesktop) {
            this.isNextAvailableOuter = this.isNextavailable('outer');
            this.updateChevronFlags();
            this.pubSubService.subscribe(`marketGroup-${this.marketsGroup.localeName}`, this.PLAYER_BETS_LOADED, (marketGroupUpdated: IConstant) => {
                if (marketGroupUpdated.localeName === this.marketsGroup.localeName) {
                    this.periodIndex = marketGroupUpdated.periodIndex;
                    this.isNextAvailableOuter = this.isNextavailable('outer');
                    this.updateChevronFlags();
                }
            });
        }
    }
    /**
     * to remove all unused markets
     * @param periodIndex
     * @returns array of market
     */
    removeUnusedMarketNames(periodIndex: number): IGroupedMarketHeader[] {
        const marketData = this.marketsGroup.periods[periodIndex].markets;
        let playerMheader = this.marketsGroup.header.map((header) => header.name);
        let fakeOutcomeArray: (string | undefined)[] = [];
        let allfakeOutcomeMarket: string[][] = [];
        if (marketData) {
            marketData.forEach((market) => {
                fakeOutcomeArray = [
                    ...fakeOutcomeArray,
                    ...market.outcomes.filter((outcome: IOutcome) => outcome.fakeOutcome).map((fakeOutcome: IOutcome) => fakeOutcome.marketsNames),
                ];
            });
            playerMheader.forEach((headerName) => {
                allfakeOutcomeMarket.push(
                    fakeOutcomeArray.filter(
                        (fakeOutcomeArrayItem): fakeOutcomeArrayItem is string =>
                            fakeOutcomeArrayItem === headerName && fakeOutcomeArrayItem !== undefined,
                    ),
                );
            });
            allfakeOutcomeMarket =
                allfakeOutcomeMarket && allfakeOutcomeMarket.filter((allfakeOutcomeItem) => allfakeOutcomeItem.length === marketData.length);
            return this.marketsGroup.header.filter(
                (header) => !allfakeOutcomeMarket.map((allfakeOutcomeMarketItem) => allfakeOutcomeMarketItem[0]).includes(header.name),
            );
        }
        return this.marketsGroup.header;
    }
    /**
     * to remove unused fakes
     * @param outcomes
     * @returns outcomes array
     */
    protected override removeUnusedFakes(outcomes: IOutcome[]): IOutcome[] {
        const headers = this.removeUnusedMarketNames(this.periodIndex);
        return outcomes.filter((outcomeItem: IOutcome) => headers.some((header: IGroupedMarketHeader) => header.sortOrder === outcomeItem.sortOrder));
    }

    /**
     * to move the the market column to next market
     */
    next(): void {
        this.gethtmlElementsforScroll();
        let divWidthwithPadding = this.oddsTablesforplayerBets!.clientWidth! + 12;
        this.isNext = this.oddsTablesforplayerBets!.scrollWidth! - this.oddsTablesforplayerBets!.scrollLeft! !== divWidthwithPadding;
        this.oddsTablesforplayerBets &&
            this.isNextavailable('inner') &&
            this.oddsTablesforplayerBets.scrollBy({ left: this.pricebtnWidth!.offsetWidth! + 4 });
        this.updateChevronFlags();
    }

    /**
     * to move the the market column to previous market
     */
    previous(): void {
        this.gethtmlElementsforScroll();
        if (this.oddsTablesforplayerBets) {
            this.oddsTablesforplayerBets.scrollLeft = this.oddsTablesforplayerBets.scrollLeft - (this.pricebtnWidth!.offsetWidth! + 4);
            this.updateChevronFlags();
        }
    }

    /**
     * if previous button is available
     * @returns boolean
     */
    isprevAvailable(): boolean {
        this.gethtmlElementsforScroll();
        return !!(this.oddsTablesforplayerBets &&
            this.oddsTablesforplayerBets.scrollLeft != 0 &&
            this.oddsTablesforplayerBets.scrollWidth > this.oddsTablesforplayerBets.clientWidth)!;
    }

    /**
     * if next button is available
     * @returns boolean
     */
    isNextavailable(locName: string): boolean {
        this.gethtmlElementsforScroll();
        if (this.oddsTablesforplayerBets) {
            let isScrollMore =
                this.oddsTablesforplayerBets.scrollWidth - this.oddsTablesforplayerBets.clientWidth - this.oddsTablesforplayerBets.scrollLeft;
            return locName == 'outer'
                ? this.checkOuterNext()
                : isScrollMore > this.ODDS_TABLE_PADDING && this.oddsTablesforplayerBets.scrollWidth > this.oddsTablesforplayerBets.clientWidth;
        }
        return false;
    }
    /**
     * to get isnextAvialble
     * @returns isnextAvialble
     */
    checkOuterNext() {
        if (this.oddsTablesforplayerBets!.scrollWidth > this.oddsTablesforplayerBets!.clientWidth) {
            this.isNextavailableInner = true;
            this.isNextAvailableOuter = true;
        } else {
            this.isNextavailableInner = false;
            this.isNextAvailableOuter = false;
        }
        this.isLoadedPB = this.pricebtnWidth !== null;
        this.isLoadedPB && this.isLoaded.emit(true);
        return this.oddsTablesforplayerBets!.scrollWidth > this.oddsTablesforplayerBets!.clientWidth;
    }

    /**
     * assign html elements
     */
    gethtmlElementsforScroll(): void {
        this.oddsTablesforplayerBets =
            this.oddsTablesforplayerBets && this.oddsTablesforplayerBets.clientWidth
                ? this.oddsTablesforplayerBets
                : this.windowRefService.document.querySelector('#' + this.marketsGroup.localeName);
        this.pricebtnWidth =
            this.pricebtnWidth && this.pricebtnWidth.offsetWidth
                ? this.pricebtnWidth
                : this.windowRefService.document.querySelector('.player-market-odds .btn-bet');
    }
    /**
     * to update the chevron flags
     */
    updateChevronFlags() {
        this.isNextavailableInner = this.isNextavailable('inner');
        this.isPrevAvailablePB = this.isprevAvailable();
        this.changeDetectorRef.detectChanges();
    }

    override ngOnDestroy() {
        super.ngOnDestroy();
        this.pubSubService.unsubscribe(`marketGroup-${this.marketsGroup.localeName}`);
    }
}
